import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { AccountSelectionResult, BankConnectionLinkResult } from '@signalvote/shared';
import { useSelectBankAccounts } from '@/hooks/queries/useBankConnections';
import { cn } from '@/lib/utils';

interface BankAccountSelectionProps {
  /** The exchange result — the connection id + the Item's selectable accounts. */
  exchange: BankConnectionLinkResult;
  /** Return to the start of the link flow (link another bank / dismiss). */
  onReset: () => void;
}

/**
 * Step 2 of the guided link flow (ADR-070, #562): after Plaid Link exchanges,
 * the treasurer picks which of the Item's accounts (checking / savings / credit)
 * to track. Confirming persists the chosen accounts and runs the first pull —
 * synced rows land in the EXISTING triage inbox (`/admin/finance/close`), so the
 * success panel points there rather than building a new inbox.
 *
 * Accounts start all-selected (the common case is "track everything at this
 * bank"); the treasurer unchecks any personal account they don't want pulled.
 */
export function BankAccountSelection({ exchange, onReset }: BankAccountSelectionProps) {
  const navigate = useNavigate();
  const { mutateAsync: selectAccounts, isPending } = useSelectBankAccounts();

  // Default: track every account the bank exposed.
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(exchange.accounts.map((a) => a.accountId)),
  );
  const [result, setResult] = useState<AccountSelectionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const toggle = (accountId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  const handleTrack = async () => {
    setError(null);
    try {
      const res = await selectAccounts({
        connectionId: exchange.connectionId,
        // Preserve the bank's account order (not Set-insertion order).
        plaidAccountIds: exchange.accounts
          .map((a) => a.accountId)
          .filter((id) => selected.has(id)),
      });
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your account selection.');
    }
  };

  // A childless Item is valid (backend allows it) — nothing to track.
  if (exchange.accounts.length === 0) {
    return (
      <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
        <p>
          No trackable accounts were found for this bank. Only checking, savings,
          and credit-card accounts can be synced.
        </p>
        <button
          type="button"
          onClick={onReset}
          className="mt-2 text-sm font-medium text-primary underline"
        >
          Try another bank
        </button>
      </div>
    );
  }

  // Success panel — synced into the existing triage inbox.
  if (result) {
    const trackedCount = result.trackedAccounts.length;
    const addedCount = result.firstPull?.added ?? 0;
    return (
      <div
        className="rounded-md border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-900"
        data-testid="bank-link-success"
      >
        <p className="font-medium">
          Linked {trackedCount} {trackedCount === 1 ? 'account' : 'accounts'}
          {exchange.institutionName ? ` at ${exchange.institutionName}` : ''}.
        </p>
        <p className="mt-1">
          {addedCount > 0
            ? `${addedCount} ${addedCount === 1 ? 'transaction' : 'transactions'} synced to your triage inbox.`
            : 'No new transactions to sync yet — future activity will arrive automatically.'}
        </p>
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate({ to: '/admin/finance/close' })}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Go to triage inbox
          </button>
          <button
            type="button"
            onClick={onReset}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Link another bank
          </button>
        </div>
      </div>
    );
  }

  // Selection step.
  return (
    <div className="rounded-md border border-border bg-muted/20 px-4 py-3 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-foreground">Choose accounts to track</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {exchange.institutionName
            ? `Accounts at ${exchange.institutionName}. `
            : ''}
          Pick the accounts whose transactions should sync into your triage inbox.
        </p>
      </div>

      <div className="space-y-2">
        {exchange.accounts.map((account) => {
          const checkboxId = `account-${account.accountId}`;
          return (
            <label
              key={account.accountId}
              htmlFor={checkboxId}
              className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2 cursor-pointer hover:bg-accent/40"
            >
              <input
                id={checkboxId}
                type="checkbox"
                className="h-4 w-4"
                checked={selected.has(account.accountId)}
                onChange={() => toggle(account.accountId)}
                data-testid={`account-checkbox-${account.accountId}`}
              />
              <span className="flex flex-col">
                <span className="text-sm text-foreground">{account.name}</span>
                <span
                  className="text-xs text-muted-foreground"
                  data-testid={`account-type-${account.accountId}`}
                >
                  {accountTypeLabel(account.type, account.subtype)}
                  {account.mask ? ` ••${account.mask}` : ''}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      {error && (
        <p className="text-sm text-red-600" data-testid="bank-link-select-error">
          {error}
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onReset}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleTrack}
          disabled={selected.size === 0 || isPending}
          className={cn(
            'rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90',
            (selected.size === 0 || isPending) && 'opacity-60 cursor-not-allowed',
          )}
        >
          {isPending ? 'Linking…' : 'Track selected accounts'}
        </button>
      </div>
    </div>
  );
}

/**
 * Turns Plaid's raw account taxonomy into a treasurer-readable label — the
 * subtype when present (e.g. `credit card` → "Credit card"), else the type.
 * Title-cases only the first letter so multi-word subtypes read naturally.
 */
function accountTypeLabel(type: string, subtype: string | null): string {
  const raw = (subtype ?? type).trim();
  if (!raw) return type;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}
